import { createBrowserRouter, useRouteError, Link } from 'react-router-dom';
import AuthGuard from '@/components/AuthGuard';
import PortalLayout from '@/components/PortalLayout';
import Login from '@/pages/Login';
import Register from '@/pages/Register';
import PendingApproval from '@/pages/PendingApproval';
import ComingSoon from '@/pages/ComingSoon';
import Dashboard from '@/pages/Dashboard';
import Catalog from '@/pages/catalog/Catalog';
import CartPage from '@/pages/cart/Cart';
import OrderHistory from '@/pages/orders/OrderHistory';
import OrderDetail from '@/pages/orders/OrderDetail';
import OrderTracking from '@/pages/orders/OrderTracking';
import Invoices from '@/pages/invoices/Invoices';
import InvoiceDetail from '@/pages/invoices/InvoiceDetail';
import Payments from '@/pages/billing/Payments';
import MakePayment from '@/pages/billing/MakePayment';
import Statement from '@/pages/billing/Statement';
import CreditMemos from '@/pages/billing/CreditMemos';
import Profile from '@/pages/account/Profile';
import Addresses from '@/pages/account/Addresses';
import Preferences from '@/pages/account/Preferences';
import ProductLabels from '@/pages/labels/ProductLabels';
import LabelDetail from '@/pages/labels/LabelDetail';
import ExceptionQueue from '@/pages/exceptions/ExceptionQueue';
import JournalEntries from '@/pages/accounting/JournalEntries';
import FixedAssetsPage from '@/pages/accounting/FixedAssets';
import BudgetsPage from '@/pages/accounting/Budgets';
import CurrencyRatesPage from '@/pages/accounting/CurrencyRates';
import FiscalPeriodsPage from '@/pages/accounting/FiscalPeriods';
import TaxCompliancePage from '@/pages/accounting/TaxCompliance';
import NotFound from '@/pages/NotFound';

// New imports
import Chargebacks from '@/pages/billing/Chargebacks';
import PaymentLinks from '@/pages/billing/PaymentLinks';
import RecurringOrders from '@/pages/orders/RecurringOrders';
import SubstitutionsPage from '@/pages/orders/Substitutions';
import ColdChainReport from '@/pages/compliance/ColdChainReport';
import ProductTrace from '@/pages/compliance/ProductTrace';
import OrganicCOC from '@/pages/compliance/OrganicCOC';
import COACertificates from '@/pages/compliance/COACertificates';
import COOL from '@/pages/compliance/COOL';
import PettyCash from '@/pages/accounting/PettyCash';
import APAgingPage from '@/pages/accounting/APAging';
import CashForecastPage from '@/pages/accounting/CashForecast';
import LoyaltyRewards from '@/pages/account/LoyaltyRewards';
import Sustainability from '@/pages/account/Sustainability';
import MarketPrices from '@/pages/analytics/MarketPrices';
import SpendingDashboard from '@/pages/analytics/SpendingDashboard';
import Contracts from '@/pages/account/Contracts';
import IntegrationDashboard from '@/pages/account/IntegrationDashboard';

// FreshByte-surpass portal pages
import MyPriceList from '@/pages/pricing/MyPriceList';
import BidResponses from '@/pages/pricing/BidResponses';
import FuturePrices from '@/pages/pricing/FuturePrices';
import PriceMatch from '@/pages/pricing/PriceMatch';
import DeliverySchedule from '@/pages/delivery/DeliverySchedule';
import DeliveryHistory from '@/pages/delivery/DeliveryHistory';
import ProvisionalCosts from '@/pages/billing/ProvisionalCosts';
import QuickReorder from '@/pages/orders/QuickReorder';
import SmartReorder from '@/pages/orders/SmartReorder';
import TeamManagement from '@/pages/account/TeamManagement';

// Payroll self-service pages
import PayStubs from '@/pages/payroll/PayStubs';
import TaxForms from '@/pages/payroll/TaxForms';
import DirectDeposit from '@/pages/payroll/DirectDeposit';
import W4Update from '@/pages/payroll/W4Update';
import LeaveBalances from '@/pages/payroll/LeaveBalances';
import FinalPayStatus from '@/pages/payroll/FinalPayStatus';
import PayrollAIAssistant from '@/pages/payroll/PayrollAIAssistant';

// AI Assistant & Insights
import AIAssistant from '@/pages/ai/AIAssistant';
import AIInsights from '@/pages/ai/AIInsights';

// Payment Plans
import PaymentPlans from '@/pages/billing/PaymentPlans';
import DisputeTracker from '@/pages/billing/DisputeTracker';
import AutoPay from '@/pages/billing/AutoPay';

// Self-service portal pages
import CreditStatus from '@/pages/account/CreditStatus';
import TaxCertificates from '@/pages/account/TaxCertificates';
import SupportCenter from '@/pages/support/SupportCenter';
import DocumentCenter from '@/pages/documents/DocumentCenter';

// Wave 2 pages
import OrderTemplates from '@/pages/orders/OrderTemplates';
import ParLevels from '@/pages/orders/ParLevels';
import DeliveryCalendar from '@/pages/delivery/DeliveryCalendar';
import QualityFeedback from '@/pages/delivery/QualityFeedback';
import ClaimsCenter from '@/pages/claims/ClaimsCenter';
import SubstitutionPrefs from '@/pages/orders/SubstitutionPrefs';
import PriceWatch from '@/pages/pricing/PriceWatch';
import SeasonalCalendar from '@/pages/catalog/SeasonalCalendar';

// School compliance pages
import BuyAmerica from '@/pages/compliance/BuyAmerica';
import FFVP from '@/pages/compliance/FFVP';
import SalesAnalysis from '@/pages/compliance/SalesAnalysis';

// Customer Feedback Insights
import CustomerFeedbackInsights from '@/pages/feedback/CustomerFeedbackInsights';

// Employee T&A self-service pages
import MyTimeDashboard from '@/pages/employee/MyTimeDashboard';
import MyShiftSwaps from '@/pages/employee/MyShiftSwaps';
import MyAttendanceHistory from '@/pages/employee/MyAttendanceHistory';
import MyPTORequests from '@/pages/employee/MyPTORequests';

// Credit & Employment applications
import CreditApplication from '@/pages/account/CreditApplication';
import NetTermsRequest from '@/pages/account/NetTermsRequest';
import StatementCycleRequest from '@/pages/account/StatementCycleRequest';
import EmploymentApplication from '@/pages/public/EmploymentApplication';
import PrivacyPolicy from '@/pages/public/PrivacyPolicy';
import PublicCreditApplication from '@/pages/public/CreditApplication';
import SMSTerms from '@/pages/public/SMSTerms';
import SMSOptInPreview from '@/pages/public/SMSOptInPreview';
import TermsOfService from '@/pages/public/TermsOfService';

// Returns & Quotes
import ReturnsPage from '@/pages/returns/ReturnsPage';
import QuotesPage from '@/pages/quotes/QuotesPage';
import RFQPage from '@/pages/quotes/RFQPage';

// Warranty
import WarrantyPage from '@/pages/warranty/WarrantyPage';

// IoT Sensors
import SensorDashboard from '@/pages/iot/SensorDashboard';

// Order Intake & Voice
import OrderIntake from '@/pages/orders/OrderIntake';
import VoicemailOrders from '@/pages/orders/VoicemailOrders';
import VoiceCallHistory from '@/pages/orders/VoiceCallHistory';
import ECommerceOrders from '@/pages/orders/ECommerceOrders';
import SmartSubstitutions from '@/pages/orders/SmartSubstitutions';

// Account pages
import SMSPreferences from '@/pages/account/SMSPreferences';
import SatisfactionDashboard from '@/pages/account/SatisfactionDashboard';
import MyActivities from '@/pages/account/MyActivities';

// Social Content
import SocialContent from '@/pages/social/SocialContent';

// Menu Planner
import MenuPlannerHub from '@/pages/menu-planner/MenuPlannerHub';
import MyMenus from '@/pages/menu-planner/MyMenus';
import MenuDetail from '@/pages/menu-planner/MenuDetail';
import RecipeToCart from '@/pages/menu-planner/RecipeToCart';
import EventPlanner from '@/pages/menu-planner/EventPlanner';
import SeasonalPicks from '@/pages/menu-planner/SeasonalPicks';
import SubstitutionFinder from '@/pages/menu-planner/SubstitutionFinder';

function RouteErrorFallback() {
  const error = useRouteError() as Error;
  return (
    <div style={{ padding: '2rem', textAlign: 'center' }}>
      <h2>This page is unavailable</h2>
      <p style={{ color: '#666', marginBottom: '1rem' }}>
        {error?.message || 'Something went wrong loading this page.'}
      </p>
      <Link to="/dashboard" style={{ color: 'var(--mantine-color-brand-6)' }}>
        Back to Dashboard
      </Link>
    </div>
  );
}

const router = createBrowserRouter([
  {
    path: '/',
    element: <ComingSoon />,
  },
  {
    path: '/apply',
    element: <EmploymentApplication />,
  },
  {
    path: '/credit-application',
    element: <PublicCreditApplication />,
  },
  {
    path: '/privacy',
    element: <PrivacyPolicy />,
  },
  {
    path: '/terms',
    element: <TermsOfService />,
  },
  {
    path: '/sms-terms',
    element: <SMSTerms />,
  },
  {
    path: '/sms-opt-in-preview',
    element: <SMSOptInPreview />,
  },
  {
    path: '/coming-soon',
    element: <ComingSoon />,
  },
  {
    path: '/login',
    element: <Login />,
  },
  {
    path: '/register',
    element: <Register />,
  },
  {
    path: '/pending',
    element: <PendingApproval />,
  },
  {
    element: <AuthGuard />,
    children: [
      {
        element: <PortalLayout />,
        errorElement: <RouteErrorFallback />,
        children: [
          { path: '/dashboard', element: <Dashboard /> },
          { path: '/shop', element: <Catalog /> },
          { path: '/cart', element: <CartPage /> },
          { path: '/orders', element: <OrderHistory /> },
          { path: '/orders/:id', element: <OrderDetail /> },
          { path: '/orders/:id/track', element: <OrderTracking /> },
          { path: '/invoices', element: <Invoices /> },
          { path: '/invoices/:id', element: <InvoiceDetail /> },
          { path: '/labels', element: <ProductLabels /> },
          { path: '/labels/:id', element: <LabelDetail /> },
          { path: '/exceptions', element: <ExceptionQueue /> },
          { path: '/accounting/journal-entries', element: <JournalEntries /> },
          { path: '/accounting/fixed-assets', element: <FixedAssetsPage /> },
          { path: '/accounting/budgets', element: <BudgetsPage /> },
          { path: '/accounting/currency', element: <CurrencyRatesPage /> },
          { path: '/accounting/fiscal-periods', element: <FiscalPeriodsPage /> },
          { path: '/accounting/tax-compliance', element: <TaxCompliancePage /> },
          { path: '/billing/payments', element: <Payments /> },
          { path: '/billing/pay', element: <MakePayment /> },
          { path: '/billing/statement', element: <Statement /> },
          { path: '/billing/credits', element: <CreditMemos /> },
          { path: '/account', element: <Profile /> },
          { path: '/account/addresses', element: <Addresses /> },
          { path: '/account/preferences', element: <Preferences /> },
          { path: '/billing/chargebacks', element: <Chargebacks /> },
          { path: '/billing/payment-links', element: <PaymentLinks /> },
          { path: '/orders/recurring', element: <RecurringOrders /> },
          { path: '/orders/substitutions', element: <SubstitutionsPage /> },
          { path: '/compliance/cold-chain', element: <ColdChainReport /> },
          { path: '/compliance/trace', element: <ProductTrace /> },
          { path: '/compliance/organic', element: <OrganicCOC /> },
          { path: '/compliance/coa', element: <COACertificates /> },
          { path: '/compliance/cool', element: <COOL /> },
          { path: '/accounting/petty-cash', element: <PettyCash /> },
          { path: '/accounting/ap-aging', element: <APAgingPage /> },
          { path: '/accounting/cash-forecast', element: <CashForecastPage /> },
          { path: '/account/loyalty', element: <LoyaltyRewards /> },
          { path: '/account/sustainability', element: <Sustainability /> },
          { path: '/analytics/market-prices', element: <MarketPrices /> },
          { path: '/analytics/spending', element: <SpendingDashboard /> },
          { path: '/account/contracts', element: <Contracts /> },
          { path: '/account/integrations', element: <IntegrationDashboard /> },
          { path: '/account/team', element: <TeamManagement /> },
          { path: '/pricing/my-prices', element: <MyPriceList /> },
          { path: '/pricing/future-prices', element: <FuturePrices /> },
          { path: '/pricing/bids', element: <BidResponses /> },
          { path: '/pricing/price-match', element: <PriceMatch /> },
          { path: '/delivery/schedule', element: <DeliverySchedule /> },
          { path: '/delivery/history', element: <DeliveryHistory /> },
          { path: '/billing/provisional-costs', element: <ProvisionalCosts /> },
          { path: '/billing/payment-plans', element: <PaymentPlans /> },
          { path: '/billing/disputes', element: <DisputeTracker /> },
          { path: '/billing/autopay', element: <AutoPay /> },
          { path: '/orders/quick-reorder', element: <QuickReorder /> },
          { path: '/orders/smart-reorder', element: <SmartReorder /> },
          { path: '/payroll/pay-stubs', element: <PayStubs /> },
          { path: '/payroll/tax-forms', element: <TaxForms /> },
          { path: '/payroll/direct-deposit', element: <DirectDeposit /> },
          { path: '/payroll/w4', element: <W4Update /> },
          { path: '/payroll/leave-balances', element: <LeaveBalances /> },
          { path: '/payroll/final-pay', element: <FinalPayStatus /> },
          { path: '/payroll/ai-assistant', element: <PayrollAIAssistant /> },
          { path: '/employee/my-time', element: <MyTimeDashboard /> },
          { path: '/employee/shift-swaps', element: <MyShiftSwaps /> },
          { path: '/employee/attendance', element: <MyAttendanceHistory /> },
          { path: '/employee/pto', element: <MyPTORequests /> },
          { path: '/ai/assistant', element: <AIAssistant /> },
          { path: '/ai/insights', element: <AIInsights /> },
          { path: '/account/credit', element: <CreditStatus /> },
          { path: '/account/tax-certificates', element: <TaxCertificates /> },
          { path: '/account/credit-application', element: <CreditApplication /> },
          { path: '/account/net-terms-request', element: <NetTermsRequest /> },
          { path: '/account/statement-cycle', element: <StatementCycleRequest /> },
          { path: '/support', element: <SupportCenter /> },
          { path: '/documents', element: <DocumentCenter /> },
          // Wave 2
          { path: '/orders/templates', element: <OrderTemplates /> },
          { path: '/orders/par-levels', element: <ParLevels /> },
          { path: '/orders/substitution-rules', element: <SubstitutionPrefs /> },
          { path: '/delivery/calendar', element: <DeliveryCalendar /> },
          { path: '/delivery/feedback', element: <QualityFeedback /> },
          { path: '/claims', element: <ClaimsCenter /> },
          { path: '/pricing/watch', element: <PriceWatch /> },
          { path: '/shop/seasonal', element: <SeasonalCalendar /> },
          // Feedback & Insights
          { path: '/feedback/insights', element: <CustomerFeedbackInsights /> },
          // School compliance
          { path: '/compliance/buy-america', element: <BuyAmerica /> },
          { path: '/compliance/ffvp', element: <FFVP /> },
          { path: '/compliance/sales-analysis', element: <SalesAnalysis /> },
          // Returns & Quotes
          { path: '/returns', element: <ReturnsPage /> },
          { path: '/quotes', element: <QuotesPage /> },
          { path: '/quotes/rfq', element: <RFQPage /> },
          // Warranty
          { path: '/warranty', element: <WarrantyPage /> },
          // IoT Sensors
          { path: '/sensors', element: <SensorDashboard /> },
          // Order Intake & Voice
          { path: '/orders/intake', element: <OrderIntake /> },
          { path: '/orders/voicemail', element: <VoicemailOrders /> },
          { path: '/orders/voice-calls', element: <VoiceCallHistory /> },
          { path: '/orders/ecommerce', element: <ECommerceOrders /> },
          { path: '/orders/smart-substitutions', element: <SmartSubstitutions /> },
          // Account extras
          { path: '/account/sms-preferences', element: <SMSPreferences /> },
          { path: '/account/satisfaction', element: <SatisfactionDashboard /> },
          { path: '/account/activities', element: <MyActivities /> },
          // Social Content
          { path: '/social', element: <SocialContent /> },
          // Menu Planner
          { path: '/menu-planner', element: <MenuPlannerHub /> },
          { path: '/menu-planner/menus', element: <MyMenus /> },
          { path: '/menu-planner/menus/:id', element: <MenuDetail /> },
          { path: '/menu-planner/recipe', element: <RecipeToCart /> },
          { path: '/menu-planner/events', element: <EventPlanner /> },
          { path: '/menu-planner/seasonal', element: <SeasonalPicks /> },
          { path: '/menu-planner/substitutes', element: <SubstitutionFinder /> },
          { path: '*', element: <NotFound /> },
        ],
      },
    ],
  },
  {
    path: '*',
    element: <ComingSoon />,
  },
]);

export default router;
